import { ChangeDetectionStrategy, Component, effect, signal, viewChild } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Bind } from "../../../decorators/bind.decorator";
import { RowData } from "../../s25-spreadsheet/types/sheet.js.types.expanded";
import { SpreadsheetComponent } from "../../s25-spreadsheet/s25.spreadsheet.component";
import { ScrollLockService } from "../../../services/scroll.lock.service";
import {
    downloadSpecialDateImportTemplate,
    runSpecialDateImport,
    SpecialDateImportUiState,
} from "../../import/special.date.import.flow";

@TypeManagerDecorator("s25-ng-special-date-bulk-upload")
@Component({
    selector: "s25-ng-special-date-bulk-upload",
    host: { class: "s25-ng-special-date-bulk-upload" },
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <p class="c-margin-bottom--single">
            Download the template, fill in holidays, closures, and academic dates, then upload and review any row errors
            before importing. Rows match on name and start date, so re-uploading updates instead of duplicating.
        </p>

        <div class="c-margin-bottom--single">
            <button class="aw-button aw-button--outline" (click)="downloadTemplate()">Download Excel Template</button>
        </div>

        @if (errors().length) {
            <ul class="ngRed c-margin-bottom--single">
                @for (error of errors(); track error) {
                    <li>{{ error }}</li>
                }
            </ul>
        }

        @if (successMessage()) {
            <div class="ngGreen c-margin-bottom--single">{{ successMessage() }}</div>
        }

        <s25-ng-spreadsheet
            class="c-margin-top--single"
            completeText="Import Special Dates"
            [widthFunc]="widthFunc"
        ></s25-ng-spreadsheet>
    `,
    standalone: false,
})
export class S25SpecialDateBulkUploadComponent {
    readonly errors = signal<string[]>([]);
    readonly successMessage = signal("");
    readonly widthFunc = (): number => Math.floor(window.innerWidth * 0.85);

    private readonly spreadsheet = viewChild(SpreadsheetComponent);

    private readonly wireSpreadsheetImport = effect(() => {
        const spreadsheet = this.spreadsheet();
        if (!spreadsheet) {
            return;
        }
        spreadsheet.onComplete = this.onComplete;
    });

    async downloadTemplate() {
        await downloadSpecialDateImportTemplate();
    }

    @Bind
    async onComplete(rows: RowData[]) {
        const state: SpecialDateImportUiState = {
            errors: [],
            successMessage: "",
        };
        await runSpecialDateImport(rows, state);
        this.errors.set(state.errors);
        this.successMessage.set(state.successMessage);
        ScrollLockService.unlockBodyScroll();
    }
}
